'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Document {
  title: string;
  type: string;
  date: string;
  size: string;
  downloadUrl: string;
}

interface Certification {
  name: string;
  issuer: string;
  validUntil: string;
  icon: string;
}

interface FinancialMetric {
  label: string;
  value: string;
  percentage: number;
  color: string;
}

interface TransparencySectionProps {
  documents: Document[];
  certifications: Certification[];
  financialMetrics: FinancialMetric[];
}

const TransparencySection: React.FC<TransparencySectionProps> = ({
  documents,
  certifications,
  financialMetrics,
}) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState<'documents' | 'certifications' | 'financials'>('documents');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <section className="py-10 sm:py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Transparency Center
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Building trust through complete transparency and accountability
            </p>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="h-96 bg-card rounded-xl shadow-subtle animate-pulse" />
          </div>
        </div>
      </section>
    );
  }

  const getColorClasses = (color: string) => {
    const colorMap: Record<string, string> = {
      primary: 'bg-primary',
      secondary: 'bg-secondary',
      accent: 'bg-accent',
      supporting: 'bg-supporting',
    };
    return colorMap[color] || colorMap.primary;
  };

  return (
    <section className="py-10 sm:py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Transparency Center
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Building trust through complete transparency and accountability
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap gap-4 mb-8 justify-center">
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 ${
                activeTab === 'documents'
                  ? 'bg-primary text-primary-foreground shadow-subtle' :'bg-card text-foreground hover:bg-muted'
              }`}
            >
              <Icon name="DocumentTextIcon" size={20} className="inline mr-2" />
              Documents
            </button>
            <button
              onClick={() => setActiveTab('certifications')}
              className={`px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 ${
                activeTab === 'certifications'
                  ? 'bg-primary text-primary-foreground shadow-subtle' :'bg-card text-foreground hover:bg-muted'
              }`}
            >
              <Icon name="ShieldCheckIcon" size={20} className="inline mr-2" />
              Certifications
            </button>
            <button
              onClick={() => setActiveTab('financials')}
              className={`px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 ${
                activeTab === 'financials' ?'bg-primary text-primary-foreground shadow-subtle' :'bg-card text-foreground hover:bg-muted'
              }`}
            >
              <Icon name="ChartBarIcon" size={20} className="inline mr-2" />
              Financial Reports
            </button>
          </div>

          <div className="bg-card rounded-xl shadow-subtle p-4 sm:p-6 lg:p-8">
            {activeTab === 'documents' && (
              <div className="space-y-4">
                {documents.map((doc, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 bg-background rounded-lg hover:shadow-subtle transition-all duration-300"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name="DocumentIcon" size={24} className="text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">{doc.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          {doc.type} • {doc.date} • {doc.size}
                        </p>
                      </div>
                    </div>
                    <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-semibold text-sm hover:bg-primary/90 transition-colors duration-300 flex items-center">
                      <Icon name="ArrowDownTrayIcon" size={18} className="mr-2" />
                      Download
                    </button>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'certifications' && (
              <div className="grid md:grid-cols-2 gap-6">
                {certifications.map((cert, index) => (
                  <div
                    key={index}
                    className="p-6 bg-background rounded-lg border-2 border-border hover:border-secondary transition-all duration-300"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name={cert.icon as any} size={32} className="text-secondary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground mb-1">{cert.name}</h4>
                        <p className="text-sm text-muted-foreground mb-2">{cert.issuer}</p>
                        <div className="flex items-center space-x-2">
                          <Icon name="CalendarIcon" size={16} className="text-accent" />
                          <span className="text-xs text-muted-foreground">
                            Valid until {cert.validUntil}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'financials' && (
              <div className="space-y-6">
                {financialMetrics.map((metric, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-foreground">{metric.label}</h4>
                      <span className="text-lg font-bold text-foreground">{metric.value}</span>
                    </div>
                    <div className="relative h-3 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`absolute top-0 left-0 h-full ${getColorClasses(metric.color)} transition-all duration-500`}
                        style={{ width: `${metric.percentage}%` }}
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">{metric.percentage}% of total budget</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TransparencySection;